import { Insert } from '@/composables/GraphQL';
import {
  ShopsNotificationsReadInputItem,
} from '@/types/generated-types/graphql';
import { TemplateEditorState } from '@/composables/template-editor/TemplateEditor';
import { AutomatedScenarioState } from '@/composables/AutomatedScenarios';
import { store } from '@/store';
import { computed, Ref } from 'vue';
import { nestCall, NestApiType } from '@/composables/nestApi';

interface NotificationArgs {
  shopId: number;
  paging?: number;
  limit?: number;
}

export const markNotificationsAsRead = async (notificationsNotRead: ShopsNotificationsReadInputItem[]) => {
  const {
    messages, status, err,
  } = await Insert<ShopsNotificationsReadInputItem>({
    name: 'ShopsNotificationsRead',
    input: notificationsNotRead,
    type: 'ShopsNotificationsReadInput',
  });

  if (err !== '' || !status) {
    // console.log(err);
  }
};

// Module-level latch so a 401 surfaced by the polling interval only triggers
// one reload — `window.location.reload()` is async (the browser unloads on
// its own schedule) and the next interval tick could otherwise queue another
// reload before the navigation actually happens.
let authReloadTriggered = false;

export const fetchNotifications = async ({ shopId, paging = 0, limit = 10 }: NotificationArgs): Promise<Array<any>|null> => {
  if (shopId <= 0) {
    return null;
  }

  // Check if statistic report is opened
  const statsEditorPageId: Ref<number> = computed(() => store.getters['statsEditor/getPageId']);

  if (TemplateEditorState.isTemplateEditorVisible || AutomatedScenarioState.isEditorVisible || statsEditorPageId.value > 0) {
    return null;
  }

  const response = await nestCall(
    'get',
    NestApiType.DEFAULT,
    '/notifications/list',
    {},
    '',
    undefined,
    true,
    {
      limit,
      offset: paging * limit,
      order: [{ field: 'date', type: 'desc' }],
      filters: [{ field: 'id_shop', value: shopId, operator: 'EQUALS' }],
    },
  );

  // 401 on the notifications poll means our JWT has been invalidated (logout
  // on another tab, token rotated server-side, session expired). Force a full
  // reload so the auth bootstrap re-runs and either silently refreshes the
  // session or bounces to the login screen — staying on a stale page would
  // leave every other API call failing the same way.
  if (response?.error?.isAuthError && response.error.status === 401) {
    if (!authReloadTriggered) {
      authReloadTriggered = true;
      window.location.reload();
    }
    return [];
  }

  if (!response || response.error) {
    return [];
  }

  return Array.isArray(response.data) ? response.data : [];
};
